import { memo, useCallback, useMemo } from "react";
import { MultiSelectTags } from "./MultiSelectTags";

/**
 * Pick one person from the directory.
 *
 * The multi-select constrained to a single value rather than a second widget:
 * the search, the highlighting and the keyboard behaviour are the ones already
 * documented and already working.
 *
 * Shared by the activity editor (a named actor) and the programme page (a
 * co-supervisor).
 */
export const SinglePersonPicker = memo(function SinglePersonPicker({
  label,
  people,
  selectedPersonId,
  onChange,
}: {
  label: string;
  people: Array<{ _id: string; fullName: string; email: string | null }>;
  selectedPersonId: string | null;
  onChange: (personId: string | null) => void;
}) {
  const options = useMemo(
    () => people.map((person) => ({
      value: String(person._id),
      label: person.fullName,
      hint: person.email ?? undefined,
    })),
    [people],
  );
  // The last one chosen wins, so picking a second replaces the first rather
  // than being silently refused.
  const handleChange = useCallback(
    (next: string[]) => onChange(next.length === 0 ? null : next[next.length - 1]!),
    [onChange],
  );

  return (
    <MultiSelectTags
      label={label}
      placeholder="Search by name or address…"
      emptyMessage="Nobody matches that."
      options={options}
      selected={selectedPersonId === null ? [] : [selectedPersonId]}
      onChange={handleChange}
    />
  );
});
